import React from 'react';
import { Link } from 'react-router-dom';
import { fmt } from '../lib';

const usd = (v) => `$${Number.isInteger(v) ? v : v.toFixed(2)}`;
const num = (n) => n.toLocaleString();

function Headline({ L }) {
  if (!L.cur) return <p className="pl-msg">Gen0 is sold out.</p>;
  if (!L.next) return <p className="pl-msg"><b>{num(L.leftInTier)}</b> Gen0 ships left in total. This is the last price tier.</p>;
  return <p className="pl-msg"><b>{num(L.leftInTier)}</b> ship{L.leftInTier === 1 ? '' : 's'} left before the price rises to <b>{usd(L.next.usd)}</b></p>;
}

// Side column for the one-screen Mint page: price now / next price / left before it rises / minted + a vertical tier list.
function SideLadder({ L }) {
  const total = L.maxSupply || L.tiers[L.tiers.length - 1].end;
  return (
    <div className={`pl pl-side u-${L.urgency}`} data-testid="price-ladder">
      <div className="pls-now">
        <small>Price now</small>
        <b data-testid="tier">{L.cur ? usd(L.cur.usd) : 'Sold out'}</b>
        <span>{L.curBnb != null ? `≈ ${fmt(L.curBnb, 5)} BNB per ship` : ''}</span>
      </div>
      <Headline L={L} />
      <div className="pls-grid">
        <div><small>Next price</small><b>{L.next ? usd(L.next.usd) : '—'}</b></div>
        <div><small>Minted</small><b data-testid="progress">{num(L.requested)}<em> / {num(total)}</em></b></div>
      </div>
      <ol className="pls-tiers">
        {L.tiers.slice().reverse().map((t) => {
          const fill = Math.max(0, Math.min(1, (L.requested - t.start) / (t.end - t.start)));
          const state = L.cur && t.i === L.cur.i ? 'cur' : L.requested >= t.end ? 'done' : 'next';
          return (
            <li key={t.i} className={`pls-t pl-${state}`} data-testid={`tier-${t.i + 1}`}>
              <b>{usd(t.usd)}</b>
              <span className="pls-bar"><span className="pl-fill" style={{ width: `${fill * 100}%` }} /></span>
              <small>{state === 'cur' ? 'NOW' : `#${num(t.start + 1)}–${num(t.end)}`}</small>
            </li>
          );
        })}
      </ol>
      <p className="pl-foot">Paid in BNB at the live Chainlink rate. A batch that crosses a step pays each ship at its own price.</p>
    </div>
  );
}

// Gen0 stepped price ladder. compact = one-line version for the Home page; side = Mint page column.
export default function PriceLadder({ L, compact, side }) {
  if (L && side) return <SideLadder L={L} />;
  if (!L) return <div className={`pl ${compact ? 'pl-compact' : ''}`} data-testid="price-ladder"><p className="pl-msg">Loading live price…</p></div>;
  const total = L.maxSupply || L.tiers[L.tiers.length - 1].end;
  const bar = (
    <div className="pl-bar">
      {L.tiers.map((t) => {
        const w = ((t.end - t.start) / total) * 100;
        const fill = Math.max(0, Math.min(1, (L.requested - t.start) / (t.end - t.start)));
        const state = L.cur && t.i === L.cur.i ? 'cur' : L.requested >= t.end ? 'done' : 'next';
        return (
          <div key={t.i} className={`pl-seg pl-${state}`} style={{ width: `${w}%` }} data-testid={`tier-${t.i + 1}`}>
            <div className="pl-fill" style={{ width: `${fill * 100}%` }} />
            <div className="pl-lbl"><b>{usd(t.usd)}</b>{!compact && <span>#{num(t.start + 1)}–{num(t.end)}</span>}</div>
            {state === 'cur' && <div className="pl-now">NOW</div>}
          </div>
        );
      })}
    </div>
  );
  if (compact) {
    return (
      <div className={`pl pl-compact u-${L.urgency}`} data-testid="price-ladder">
        <div className="pl-row">
          <div className="pl-price"><small>Gen0 price now</small><b>{L.cur ? usd(L.cur.usd) : 'Sold out'}</b>{L.curBnb != null && <span>≈ {fmt(L.curBnb, 5)} BNB</span>}</div>
          <div style={{ flex: 1 }}>{bar}<Headline L={L} /></div>
          <Link to="/mint" className="cta-btn pl-cta">Mint now{L.cur ? ` at ${usd(L.cur.usd)}` : ''}</Link>
        </div>
      </div>
    );
  }
  return (
    <div className={`pl u-${L.urgency}`} data-testid="price-ladder">
      <div className="pl-top">
        <div className="pl-price"><small>Price now · tier {L.cur ? L.cur.i + 1 : L.tiers.length} of {L.tiers.length}</small>
          <b data-testid="tier">{L.cur ? usd(L.cur.usd) : 'Sold out'}</b>
          <span>{L.curBnb != null ? `≈ ${fmt(L.curBnb, 5)} BNB per ship` : 'BNB price loading…'}</span></div>
        <div className="pl-price pl-nextp"><small>Next tier</small>
          <b>{L.next ? usd(L.next.usd) : '—'}</b>
          <span>{L.next ? (L.nextBnb != null ? `≈ ${fmt(L.nextBnb, 5)} BNB per ship` : '') : 'no further rises'}</span></div>
        <div className="pl-price"><small>Minted</small><b>{num(L.requested)}</b><span>of {num(total)} ships</span></div>
      </div>
      <Headline L={L} />
      {bar}
      <p className="pl-foot">The price goes up in steps as ships are minted. You pay in BNB at the live Chainlink BNB/USD rate. If your mint crosses into the next tier, each ship is charged at its own tier price. Live from the mint contract.</p>
    </div>
  );
}
